import { useState, useEffect, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import axios from "axios";
import { API, STADIA_TILE_URL, emergencyIcon, hospitalIcon } from "./utils";
import { HospitalPopup } from "./HospitalPopup";
import { HydrantLayer } from "./HydrantLayer";

// Module-scoped cache for Stadia /search responses (key = lowercased address).
// Same address is never re-geocoded even across LocationMap remounts.
const GEOCODE_CACHE = new Map();

// MapFlyTo flies the map to (lat, lng). We pass primitive lat/lng (not an array)
// so the effect only re-runs when the coords actually change — prevents the map
// from "shaking" when the parent re-renders with a new array reference.
function MapFlyTo({ lat, lng, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.flyTo([lat, lng], zoom, { duration: 1 });
  }, [lat, lng, zoom, map]);
  return null;
}

export function LocationMap({ location, apt, onCoordsChange, onResolvedAddress, preResolvedCoords }) {
  const STADIA_KEY = process.env.REACT_APP_STADIA_API_KEY;
  // Store coords as {lat, lng} primitives instead of a [lat, lng] array so we can
  // do cheap value equality and avoid thrashing on parent re-renders.
  const [coords, setCoords] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const lastResolvedRef = useRef(null); // last address we already resolved
  const abortRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/hospitals`).then((r) => setHospitals(r.data.filter((h) => h.lat && h.lng))).catch(() => {});
  }, []);

  // Normalize preResolvedCoords into primitive lat/lng so the effect doesn't
  // re-fire just because the parent handed us a new array reference with the
  // same numeric values.
  const preLat = preResolvedCoords && preResolvedCoords.length === 2 ? preResolvedCoords[0] : null;
  const preLng = preResolvedCoords && preResolvedCoords.length === 2 ? preResolvedCoords[1] : null;

  useEffect(() => {
    if (!location || location.length < 5) {
      if (coords !== null) setCoords(null);
      if (onCoordsChange) onCoordsChange(null);
      lastResolvedRef.current = null;
      return;
    }
    const key = location.trim().toLowerCase();

    // Layer 1 — parent supplied resolved coords (autocomplete pick). Trust + cache.
    // Early return if we already committed these exact values — prevents the
    // render-loop that caused the map to "shake".
    if (preLat != null && preLng != null) {
      if (lastResolvedRef.current === key && coords && coords.lat === preLat && coords.lng === preLng) {
        return;
      }
      setCoords({ lat: preLat, lng: preLng });
      if (onCoordsChange) onCoordsChange([preLat, preLng]);
      GEOCODE_CACHE.set(key, { lat: preLat, lng: preLng });
      lastResolvedRef.current = key;
      return;
    }

    // Layer 2 — we already resolved this exact string this session.
    if (lastResolvedRef.current === key && coords) return;

    // Layer 3 — module cache hit (different LocationMap instance resolved it before).
    if (GEOCODE_CACHE.has(key)) {
      const c = GEOCODE_CACHE.get(key);
      if (!coords || coords.lat !== c.lat || coords.lng !== c.lng) {
        setCoords({ lat: c.lat, lng: c.lng });
        if (onCoordsChange) onCoordsChange([c.lat, c.lng]);
      }
      lastResolvedRef.current = key;
      return;
    }

    // Layer 4 — real Stadia call, debounced 600ms, cancel on new keystroke.
    const timer = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setGeocoding(true);
      const params = new URLSearchParams({
        text: location,
        "focus.point.lat": "40.6782",
        "focus.point.lon": "-73.9442",
        "boundary.rect.min_lat": "40.49",
        "boundary.rect.min_lon": "-74.27",
        "boundary.rect.max_lat": "40.92",
        "boundary.rect.max_lon": "-73.68",
        api_key: STADIA_KEY,
      });
      fetch(`https://api.stadiamaps.com/geocoding/v1/search?${params.toString()}&layers=address,street`, { signal: controller.signal })
        .then((r) => r.json())
        .then((data) => {
          if (data.features && data.features.length > 0) {
            const feat = data.features[0];
            const [lng, lat] = feat.geometry.coordinates;
            setCoords({ lat, lng });
            if (onCoordsChange) onCoordsChange([lat, lng]);
            GEOCODE_CACHE.set(key, { lat, lng });
            lastResolvedRef.current = key;
            if (onResolvedAddress && feat.properties) {
              const p = feat.properties;
              const resolved = p.label || p.name || location;
              onResolvedAddress(resolved);
            }
          }
        }).catch(() => {}).finally(() => setGeocoding(false));
    }, 600);
    return () => clearTimeout(timer);
    // NOTE: intentionally omit onCoordsChange / onResolvedAddress / coords from deps.
    // The parent passes them as inline arrows → they change on every render; including
    // them would fire this effect (and re-run `setCoords`) on every parent keystroke,
    // causing the map to shake.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, preLat, preLng, STADIA_KEY]);

  // Memoize the center tuple so MapContainer doesn't receive a new prop ref on
  // unrelated parent re-renders (prevents the map from "shaking"). MUST be called
  // unconditionally (before any early return) per rules-of-hooks.
  const mapCenter = useMemo(
    () => (coords ? [coords.lat, coords.lng] : [40.6782, -73.9442]),
    [coords?.lat, coords?.lng]
  );

  if (!location) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#888", fontStyle: "italic", fontSize: 14 }}>Please select a location, for it to appear</div>;

  const zoom = coords ? 16 : 12;
  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }} data-testid="location-map">
      <MapContainer center={mapCenter} zoom={zoom} style={{ width: "100%", height: "100%" }} zoomControl={true} attributionControl={false}>
        <TileLayer url={STADIA_TILE_URL} />
        <HydrantLayer />
        <MapFlyTo lat={coords?.lat} lng={coords?.lng} zoom={zoom} />
        {coords && <Marker position={[coords.lat, coords.lng]} icon={emergencyIcon}><Popup><strong>{location}</strong>{apt ? `, APT ${apt}` : ""}</Popup></Marker>}
        {hospitals.map((h) => <Marker key={h.id} position={[h.lat, h.lng]} icon={hospitalIcon}><Popup><HospitalPopup h={h} /></Popup></Marker>)}
      </MapContainer>
      {geocoding && <div style={{ position: "absolute", top: 8, left: 8, zIndex: 1000, background: "rgba(0,0,0,0.7)", color: "#00BFFF", padding: "3px 10px", fontSize: 11 }}>Locating...</div>}
      {coords && <div style={{ position: "absolute", bottom: 8, left: 8, zIndex: 1000, background: "rgba(0,0,0,0.7)", color: "#FFF", padding: "4px 10px", fontSize: 12 }}>{location}{apt ? ` APT ${apt}` : ""}</div>}
    </div>
  );
}
